/**
 * The benches a scene can be set on, and the question each one serves.
 *
 * See docs/FeatureDesigns/question-scenes/question-scenes-target.md for the design.
 *
 * One registry and four generic operations, never a tool per bench: adding the
 * circuits bench is a manifest, not new plumbing, and a prompt only ever
 * carries the one bench it is compiling for.
 */
import { mechanicsBench } from './mechanics';
import { opticsBench } from './optics';
import { BenchId, BenchManifest, ReadoutDecl } from './types';

// The manifests are typed by their own setup; the registry holds them erased.
const BENCHES: BenchManifest<never, unknown>[] = [
    mechanicsBench as unknown as BenchManifest<never, unknown>,
    opticsBench as unknown as BenchManifest<never, unknown>,
];

export const allBenches = (): readonly BenchManifest<never, unknown>[] => BENCHES;

export function benchOf(id: string): BenchManifest<never, unknown> | undefined {
    return BENCHES.find(b => b.bench === id);
}

/** The benches whose chapters include this module. Routing is fixed by chapter. */
export function findBenches(moduleId: string): BenchId[] {
    return BENCHES.filter(b => b.covers.includes(moduleId)).map(b => b.bench);
}

export interface BenchDescription {
    bench: BenchId;
    title: string;
    specVersion: number;
    exact: boolean;
    vocabulary: string;
    readouts: ReadoutDecl[];
    assumptions: string[];
}

/** Everything a compiler needs to know about one bench, and nothing about the others. */
export function describeBench(id: string): BenchDescription | undefined {
    const b = benchOf(id);
    if (!b) return undefined;
    return {
        bench: b.bench, title: b.title, specVersion: b.specVersion, exact: b.exact,
        vocabulary: b.vocabulary, readouts: b.readouts, assumptions: b.assumptions,
    };
}
